import { FC, useContext, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Stack, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { buildIncludeParams } from '~/core/shared/api';
import { useApplicationDocs } from '~/core/shared/api/middle-office/application';
import { useRequestRequiredApplicationDocs } from '~/core/shared/api/middle-office/application/hooks/useRequestRequiredApplicationDocs';
import { Dialog } from '~/core/shared/components';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useTableParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Doc, Sorting } from '~/core/shared/types';
import { DocsTabContext } from '../application/tabs';
import { ReactTable } from '../table';

type DocsRequiredRequestPayload = {
  docs: string[];
};

type DocsRequestRequiredModalProps = {
  includes?: string[];
};

const getColumns = (): ColumnDef<Doc>[] => [
  {
    accessorKey: 'type',
    cell({ row }) {
      return <Text>{row.original.type}</Text>;
    },
    header: 'Type',
  },
  {
    accessorKey: 'individual',
    cell({ row }) {
      // @TODO: this needs to be refactored to not access antero-specific definition: individual OR we pass columns as parameter instead
      return (
        <Text>
          {row.original.individual ? row.original.individual.docsName : '-'}
        </Text>
      );
    },
    header: 'Document owner',
  },
  {
    accessorKey: 'name',
    cell({ row }) {
      return <Text>{row.original.name ? row.original.name : '-'}</Text>;
    },
    header: 'Document name',
  },
];

const columns = getColumns();
const defaultSorting: Sorting = { column: 'updated_at', direction: 'desc' };

export const DocsRequestRequiredModal: FC<DocsRequestRequiredModalProps> = ({
  includes = [],
}) => {
  const { uuid, definition, docsRequestRequiredModalApi } =
    useContext(DocsTabContext);

  const { requestRequiredDocs } = useRequestRequiredApplicationDocs(definition);

  const { tableProps, searchParams } = useTableParams(defaultSorting, 100);

  const docsParams = useMemo(
    () => ({
      ...(searchParams as Record<string, string>),
      ...buildIncludeParams(includes),
    }),
    [searchParams, includes],
  );

  const { docs, meta, isSuccess } = useApplicationDocs(
    uuid,
    definition,
    docsParams,
  );

  const docsNeedsToBeUploaded: Doc[] = useMemo(
    () => docs?.filter((doc) => doc.file === undefined) ?? EMPTY_ARR,
    [docs],
  );

  const form = useForm<DocsRequiredRequestPayload>({});

  const onSubmit = useEventHandler(async (): Promise<void> => {
    await requestRequiredDocs(uuid);
    docsRequestRequiredModalApi.onClose();
  });

  const hasRequiredDocs = useMemo(
    () => docsNeedsToBeUploaded.length !== 0,
    [docsNeedsToBeUploaded.length],
  );

  return (
    <Dialog
      header="Send document reminder"
      onOpenChange={docsRequestRequiredModalApi.onOpenChange}
      open={docsRequestRequiredModalApi.open}
      scrollBehavior="inside"
      size="cover"
      subHeader="An email will be sent to the broker listing the outstanding documents below and requesting their upload."
    >
      <FormProvider {...form}>
        <Stack as="form" gap={4} onSubmit={form.handleSubmit(onSubmit)}>
          {isSuccess && (
            <ReactTable
              columns={columns}
              data={docsNeedsToBeUploaded}
              meta={meta}
              {...tableProps}
            />
          )}

          <Stack direction="row" justifyContent="end">
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={docsRequestRequiredModalApi.onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              disabled={!hasRequiredDocs}
              fontSize="md"
              fontWeight="bold"
              type="submit"
              variant="solid"
            >
              Send reminder
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};
